/**
 * The MCP tab's arguments form (utils/mcpTesterModel.js): a field per property of a tool's input schema (or a prompt's
 * arguments), with its type, whether it is required and its description, or the raw JSON of the whole arguments
 * object ("Raw JSON"). Switching keeps what was typed: the form's arguments become the JSON, and valid JSON fills
 * the form back.
 */
import React from 'react';
import PropTypes from 'prop-types';

export default function SchemaForm({ fields, values, errors = {}, raw, rawText, rawError = null, onValues, onRaw, onRawText, idPrefix = 'mcp-arg' }) {
  return (
    <div className="mcp-form" data-testid="mcp-form">
      <div className="mcp-form-head">
        <span className="data-label">Arguments</span>
        <span aria-label="How to edit the arguments" className="mcp-toggle" role="group">
          <button aria-pressed={!raw} className={!raw ? 'is-on' : ''} disabled={!fields.length} onClick={() => onRaw(false)} type="button">Form</button>
          <button aria-pressed={raw} className={raw ? 'is-on' : ''} data-testid="mcp-raw-toggle" onClick={() => onRaw(true)} type="button">Raw JSON</button>
        </span>
      </div>
      {raw && (
        <>
          <label className="data-sr" htmlFor={`${idPrefix}-raw`}>Arguments as JSON</label>
          <textarea className="data-input data-mono mcp-raw" data-testid="mcp-raw" id={`${idPrefix}-raw`} onChange={(event) => onRawText(event.target.value)} rows={8} spellCheck={false} value={rawText} />
          {rawError && <p className="data-error-text" role="alert">{rawError}</p>}
        </>
      )}
      {!raw && !fields.length && <p className="data-muted mcp-none">No arguments.</p>}
      {!raw && fields.map((field) => {
        const id = `${idPrefix}-${field.name}`;
        const set = (value) => onValues({ ...values, [field.name]: value });
        const error = errors[field.name];
        return (
          <div className={`mcp-field${error ? ' is-bad' : ''}`} key={field.name}>
            <label className="mcp-field-label" htmlFor={id}>
              <span className="data-mono">{field.name}</span>
              {field.required && <span aria-label="required" className="mcp-required">*</span>}
              <span className="mcp-field-type">{field.kind === 'enum' ? 'choice' : field.type}</span>
            </label>
            {field.kind === 'boolean' && (
              <input checked={values[field.name] === true} id={id} onChange={(event) => set(event.target.checked)} type="checkbox" />
            )}
            {field.kind === 'enum' && (
              <select className="data-input" id={id} onChange={(event) => set(event.target.value)} value={values[field.name] ?? ''}>
                <option value="">{field.required ? 'Choose…' : '(none)'}</option>
                {field.options.map((option) => <option key={String(option)} value={String(option)}>{String(option)}</option>)}
              </select>
            )}
            {(field.kind === 'text' || field.kind === 'number' || field.kind === 'integer') && (
              <input
                className="data-input data-mono"
                id={id}
                inputMode={field.kind === 'text' ? undefined : 'decimal'}
                onChange={(event) => set(event.target.value)}
                placeholder={field.schema.format || field.schema.pattern || ''}
                spellCheck={false}
                value={values[field.name] ?? ''}
              />
            )}
            {field.kind === 'json' && (
              <textarea className="data-input data-mono" id={id} onChange={(event) => set(event.target.value)} placeholder="JSON" rows={3} spellCheck={false} value={values[field.name] ?? ''} />
            )}
            {field.description && <span className="mcp-field-desc">{field.description}</span>}
            {error && <span className="data-error-text" role="alert">{error}</span>}
          </div>
        );
      })}
    </div>
  );
}

SchemaForm.propTypes = {
  errors: PropTypes.object,
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  idPrefix: PropTypes.string,
  onRaw: PropTypes.func.isRequired,
  onRawText: PropTypes.func.isRequired,
  onValues: PropTypes.func.isRequired,
  raw: PropTypes.bool.isRequired,
  rawError: PropTypes.string,
  rawText: PropTypes.string.isRequired,
  values: PropTypes.object.isRequired,
};
